import type { Degree, Distance, Margins, Rectangle, SpatialRectangle } from "@pdfking/math"
import type { EnumType } from "@pdfking/structs"
import type { Atom } from "@pdfking/tfunc"
import { YGAlign, YGFlexDirection, YGJustify } from "typeflex/src/enums"

export const enum Align {
  Center = YGAlign.Center,
  FlexEnd = YGAlign.FlexEnd,
  FlexStart = YGAlign.FlexStart,
  Stretch = YGAlign.Stretch,
}

export const enum Direction {
  Column = YGFlexDirection.Column,
  Row = YGFlexDirection.Row,
}

export const enum Justify {
  Center = YGJustify.Center,
  SpaceBetween = YGJustify.SpaceBetween,
  SpaceEvenly = YGJustify.SpaceEvenly,
}

export const enum FlexEnum {
  Container = "container",
  Page = "page",
}

/** What a page carries through the layout: the block it is cut with, how far
 * it is turned, its padding — in the page's own frame, not the sheet's, so a
 * turned page carries it upright — the side it prints on, the pages that
 * land on it, and whatever its layout was given to carry. */
type PageData<D> = {
  block: number
  data: D | undefined
  guid: Atom[]
  is_front: boolean
  padding: Margins<Distance>
  rotation: Degree
}

/** A cell of the tree: the subject plus its padding, turned or not. */
export type Page<D = unknown> = EnumType<FlexEnum.Page, PageData<D> & Rectangle<Distance>>

/** A laid out cell, placed on the sheet. */
export type SpatialPage<D = unknown> = PageData<D> & SpatialRectangle<Distance>

/** A laid out cell with its share of the sheet around it. */
export type BledPage<D = unknown> = { bleed: Margins<Distance> } & SpatialPage<D>

export type Container<D = unknown> = EnumType<
  FlexEnum.Container,
  {
    align: Align
    children: (Container<D> | Page<D>)[]
    data: D | undefined
    direction: Direction
    /** The side the container faces. */
    is_front: boolean
    height?: Distance
    justify: Justify
    /** Kept clear inside the box, on the sheet's own edges. */
    padding: Margins<Distance>
    /** The turn the container stands at, for whoever draws in its frame. */
    rotation: Degree
    width?: Distance
  }
>
